import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SchemaOrg from '../../components/feature/SchemaOrg';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${siteUrl}/about#webpage`,
  url: `${siteUrl}/about`,
  name: 'About Us – Simsers Affiliate | Honest Product Reviews',
  description: 'Simsers Affiliate provides honest, straightforward product reviews to help you make informed decisions. No fluff, just real insights across 23 niches.',
  isPartOf: { '@id': `${siteUrl}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
    ],
  },
  datePublished: '2025-01-01',
  dateModified: '2026-02-08',
};

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About Us – Simsers Affiliate | Honest Product Reviews';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Simsers Affiliate provides honest, straightforward product reviews to help you make informed decisions. No fluff, just real insights across 23 niches.');
    }
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'simsers affiliate, product reviews, honest reviews, affiliate site');
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/about`);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SchemaOrg schema={aboutSchema} id="schema-about" />
      <Header />

      <main className="flex-1 pt-24">
        {/* Hero Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <h1 className="text-4xl lg:text-5xl font-crimson font-bold text-gray-900 mb-8">
              About Simsers Affiliate
            </h1>
            
            <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
              <p>
                We review products so you don't have to waste your time and money figuring out what works.
              </p>
              
              <p>
                Every product on this site has been researched, analyzed, and broken down into what actually matters—does it work, is it worth the price, and who is it really for.
              </p>

              <p>
                We cover a range of categories including betting strategies, business tools, health products, and more. Our goal is simple: give you the information you need to make a smart decision.
              </p>
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="py-16 lg:py-24 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl font-crimson font-bold text-gray-900 mb-8">
              How We Review Products
            </h2>
            
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                We dig into each product's sales page, features, pricing, and user feedback. We look at what the product promises versus what it actually delivers.
              </p>
              
              <p>
                Every review includes pros and cons, who the product is best suited for, and practical tips on how to get the most out of it.
              </p>

              <p>
                We're not here to sell you everything—we're here to help you avoid the duds and find what actually works for your situation.
              </p>
            </div>
          </div>
        </section>

        {/* Affiliate Disclosure */}
        <section className="py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl font-crimson font-bold text-gray-900 mb-8">
              Affiliate Disclosure
            </h2>
            
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                Yes, we earn commissions when you purchase through our links. That's how we keep this site running.
              </p>
              
              <p>
                But here's the thing—we don't recommend products just because they pay well. A bad recommendation hurts our credibility, and credibility is all we have.
              </p>

              <p>
                If a product isn't worth your money, we'll tell you. Simple as that.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-br from-orange-50 to-teal-50">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-3xl font-crimson font-bold text-gray-900 mb-6">
              Ready to Find What You're Looking For?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Browse our reviews and find products that actually deliver.
            </p>
            <Link
              to="/"
              className="inline-block px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Browse Reviews
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
